package dev.wyns.ventrova.app

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import dev.wyns.ventrova.data.PlaceRepository
import dev.wyns.ventrova.data.model.BoundingBox
import dev.wyns.ventrova.data.model.GeoPoint
import dev.wyns.ventrova.data.model.MenuItem
import dev.wyns.ventrova.data.model.Place
import dev.wyns.ventrova.data.model.PlaceCategory
import dev.wyns.ventrova.data.model.VeganLevel
import dev.wyns.ventrova.platform.KeyValueStore
import dev.wyns.ventrova.platform.PlaceSuggestion
import dev.wyns.ventrova.platform.SuggestionService
import kotlin.time.Clock
import kotlin.time.Duration.Companion.minutes
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.mapLatest
import kotlinx.coroutines.flow.onStart
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

/** Shared state and actions of the whole app; each platform only lays it out differently. */
@OptIn(FlowPreview::class, kotlinx.coroutines.ExperimentalCoroutinesApi::class)
class AppViewModel(
    private val repository: PlaceRepository,
    private val store: KeyValueStore,
    private val suggestions: SuggestionService,
) : ViewModel() {

    private data class Viewport(val bounds: BoundingBox, val center: GeoPoint)

    private val viewport = MutableStateFlow<Viewport?>(null)
    private val filters = MutableStateFlow(Filters())
    private val favoriteIds = MutableStateFlow(store.getStringSet(KEY_FAVORITES))
    private val userLocation = MutableStateFlow<GeoPoint?>(null)
    private val selectedId = MutableStateFlow<String?>(null)
    private val detailId = MutableStateFlow<String?>(null)
    private val query = MutableStateFlow("")
    private val ui = MutableStateFlow(
        LocalState(showOnboarding = !store.getBoolean(KEY_ONBOARDING_DONE)),
    )

    private data class LocalState(
        val showOnboarding: Boolean,
        val suggestion: SuggestionForm = SuggestionForm(),
        val message: AppMessage? = null,
    )

    /** Ticks every minute so open/closed states stay current. */
    private val now = flow {
        while (true) {
            emit(Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()))
            delay(1.minutes)
        }
    }

    /** Null until the first query for the visible area has returned. */
    private val viewportPlaces = viewport.filterNotNull()
        .debounce(150)
        .mapLatest<Viewport, List<Place>?> { repository.placesIn(it.bounds) }
        .onStart { emit(null) }

    /** Null until the favorites are loaded. */
    private val favoritePlaces = favoriteIds
        .mapLatest<Set<String>, List<Place>?> { repository.placesById(it) }
        .onStart { emit(null) }

    /** Results paired with the query they belong to, so a stale result shows as searching. */
    private val searchMatches = query.debounce(200)
        .mapLatest { it to repository.search(it) }
        .onStart { emit("" to emptyList()) }

    /** Places the selection or detail may refer to, also outside the viewport (e.g. favorites). */
    private val knownPlaces = MutableStateFlow<Map<String, Place>>(emptyMap())

    private val context = combine(userLocation, viewport, favoriteIds, now) { location, viewport, favorites, now ->
        ItemContext(location ?: viewport?.center ?: DefaultCenter, location, favorites, now)
    }

    private data class ItemContext(
        val reference: GeoPoint,
        val userLocation: GeoPoint?,
        val favorites: Set<String>,
        val now: LocalDateTime,
    )

    private fun ItemContext.item(place: Place) = PlaceItem(
        place = place,
        distanceMeters = place.location.distanceTo(reference),
        openStatus = place.openingHours?.statusAt(now),
        isFavorite = place.id in favorites,
    )

    private val lists = combine(viewportPlaces, favoritePlaces, searchMatches, filters, context) { inViewOrNull, favoritesOrNull, (matchedQuery, matches), filters, context ->
        val inView = inViewOrNull.orEmpty()
        val favorites = favoritesOrNull.orEmpty()
        knownPlaces.update { known -> known + (inView + favorites + matches).associateBy { it.id } }
        val items = inView.map { context.item(it) }.sortedBy { it.distanceMeters }
        Lists(
            isLoading = inViewOrNull == null,
            favoritesLoading = favoritesOrNull == null,
            matchedQuery = matchedQuery,
            nearby = items.filter { filters.matches(it) },
            levelCounts = items.filter { filters.matches(it, ignoreLevels = true) }.groupingBy { it.place.veganLevel }.eachCount(),
            categoryCounts = items.filter { filters.matches(it, ignoreCategories = true) }.groupingBy { it.place.category }.eachCount(),
            favorites = favorites.map { context.item(it) }.sortedBy { it.distanceMeters },
            searchResults = matches.map { context.item(it) },
        )
    }

    private data class Lists(
        val isLoading: Boolean,
        val favoritesLoading: Boolean,
        val matchedQuery: String,
        val nearby: List<PlaceItem>,
        val levelCounts: Map<VeganLevel, Int>,
        val categoryCounts: Map<PlaceCategory, Int>,
        val favorites: List<PlaceItem>,
        val searchResults: List<PlaceItem>,
    )

    /** The menu of the open detail, paired with its place id; the items are null while loading. */
    private val detailMenu = detailId
        .flatMapLatest { id ->
            flow {
                if (id == null) return@flow emit(null to null)
                emit(id to null)
                emit(id to repository.menuFor(id))
            }
        }
        .onStart { emit(null to null) }

    private data class Selection(val selected: PlaceItem?, val detail: PlaceItem?, val detailMenu: List<MenuItem>?)

    private val selection = combine(selectedId, detailId, knownPlaces, context, detailMenu) { selected, detail, known, context, (menuId, menu) ->
        Selection(
            selected = known[selected]?.let { context.item(it) },
            detail = known[detail]?.let { context.item(it) },
            detailMenu = menu.takeIf { menuId == detail },
        )
    }

    val state: StateFlow<AppUiState> = combine(
        lists,
        selection,
        combine(filters, query, ui, context) { filters, query, ui, context -> Quad(filters, query, ui, context) },
    ) { lists, (selected, detail, detailMenu), (filters, query, ui, context) ->
        AppUiState(
            isLoading = lists.isLoading,
            favoritesLoading = lists.favoritesLoading,
            isSearching = query.isNotBlank() && lists.matchedQuery != query,
            showOnboarding = ui.showOnboarding,
            filters = filters,
            nearby = lists.nearby,
            levelCounts = lists.levelCounts,
            categoryCounts = lists.categoryCounts,
            selected = selected,
            detail = detail,
            detailMenu = detailMenu,
            favorites = lists.favorites,
            query = query,
            searchResults = if (query.isBlank()) emptyList() else lists.searchResults,
            userLocation = context.userLocation,
            referencePoint = context.reference,
            suggestion = ui.suggestion,
            message = ui.message,
        )
    }.stateIn(viewModelScope, SharingStarted.Eagerly, AppUiState(showOnboarding = ui.value.showOnboarding))

    private data class Quad<A, B, C, D>(val a: A, val b: B, val c: C, val d: D)

    // Map

    fun onViewportChanged(bounds: BoundingBox, center: GeoPoint) {
        viewport.value = Viewport(bounds, center)
    }

    fun onUserLocation(location: GeoPoint?) {
        userLocation.value = location
    }

    // Onboarding

    fun completeOnboarding(locationShared: Boolean) {
        store.putBoolean(KEY_ONBOARDING_DONE, true)
        ui.update { it.copy(showOnboarding = false) }
        if (!locationShared) showMessage(AppMessage.Kind.LocationOff)
    }

    // Filters

    fun toggleLevel(level: VeganLevel) = updateFilters {
        it.copy(levels = if (level in it.levels) it.levels - level else it.levels + level)
    }

    fun setStrictness(stop: StrictnessStop) = updateFilters { it.copy(levels = stop.levels) }

    fun toggleOpenNow() = updateFilters { it.copy(openNow = !it.openNow) }

    fun setRadius(radiusKm: Double?) = updateFilters { it.copy(radiusKm = radiusKm) }

    fun cycleRadius() = updateFilters {
        it.copy(radiusKm = RadiusOptions[(RadiusOptions.indexOf(it.radiusKm) + 1) % RadiusOptions.size])
    }

    fun toggleCategory(category: PlaceCategory) = updateFilters {
        it.copy(categories = if (category in it.categories) it.categories - category else it.categories + category)
    }

    fun clearFilters() = updateFilters { Filters() }

    private fun updateFilters(change: (Filters) -> Filters) {
        filters.update(change)
        selectedId.value = null
    }

    // Selection and detail

    fun selectPlace(id: String?) {
        selectedId.value = id
    }

    fun openDetail(id: String) {
        selectedId.value = id
        detailId.value = id
    }

    fun closeDetail() {
        detailId.value = null
    }

    // Favorites

    fun toggleFavorite(id: String) {
        val wasFavorite = id in favoriteIds.value
        favoriteIds.update { if (wasFavorite) it - id else it + id }
        store.putStringSet(KEY_FAVORITES, favoriteIds.value)
        showMessage(if (wasFavorite) AppMessage.Kind.FavoriteRemoved else AppMessage.Kind.FavoriteSaved)
    }

    // Search

    fun setQuery(value: String) {
        query.value = value
    }

    // Suggest a place

    fun updateSuggestion(change: (SuggestionForm) -> SuggestionForm) {
        ui.update { it.copy(suggestion = change(it.suggestion)) }
    }

    fun resetSuggestion() = updateSuggestion { SuggestionForm() }

    fun sendSuggestion() {
        val form = ui.value.suggestion
        val level = form.level
        if (!form.canSend || level == null) {
            updateSuggestion { it.copy(triedToSend = true) }
            return
        }
        viewModelScope.launch {
            suggestions.submit(PlaceSuggestion(form.name.trim(), form.address.trim(), form.category, level, form.note.trim()))
            updateSuggestion { it.copy(sent = true) }
        }
    }

    // Fry oil

    /** "Do you know? Let us know" on a chip shop whose fry oil is unknown. */
    fun requestFryOilCheck(id: String) {
        viewModelScope.launch { suggestions.requestFryOilCheck(id) }
        showMessage(AppMessage.Kind.FryOilCheckRequested)
    }

    // Messages

    fun showMessage(kind: AppMessage.Kind) {
        ui.update { it.copy(message = AppMessage(kind, Clock.System.now().toEpochMilliseconds())) }
    }

    fun consumeMessage(message: AppMessage) {
        ui.update { if (it.message == message) it.copy(message = null) else it }
    }

    private companion object {
        const val KEY_ONBOARDING_DONE = "onboarding_done"
        const val KEY_FAVORITES = "favorites"
    }
}
